<template>
  <div>
      <div class="factories_list">
          <template v-for="(order_factory, orderFactoryIndex) in order_factories">
              <button :key="`${order_factory.id}_${orderFactoryIndex}`" @click="handleFactoryUpdate(order_factory)"
                  :class="{ 'active_factory': active_factory.id == order_factory.id }">
                  {{ order_factory.name }}
              </button>
          </template>
      </div>
      <div v-if="show_loader"
          class="container-fluid spinner-container absolute d-flex justify-content-center align-items-center">
          <div class="spinner"></div>
      </div>
      <div v-else class="container-fluid mt-3" style="background-color: #eeeeee;" @dragover.prevent="isDragging = true"
          @dragleave.prevent="handleDragLeave">
          <!-- Header -->
          <div class="d-flex justify-content-end align-items-center mb-3 gap-1 flex-wrap"
              v-if="!share_point_db_settings_not_found">
              <CButton class="w-md-100" color="success" @click="$refs.fileInput.click()">Upload or drop</CButton>
              <CButton class="w-md-100" color="success" @click="openCreateFolderModal">Create Folder</CButton>
          </div>

          <!-- Breadcrumbs -->
          <b-breadcrumb class="custom-breadcrumb" v-if="drive_bread_crumbs?.length">
              <template v-for="(drive_bread_crumbs_item, driveBreadCrumbsItemIndex) in drive_bread_crumbs">
                  <b-breadcrumb-item :key="drive_bread_crumbs_item.name + '_' + driveBreadCrumbsItemIndex"
                      :active="drive_bread_crumbs_item.is_active"
                      @click=" !drive_bread_crumbs_item.is_active ? getSharePointFilesAtPath(drive_bread_crumbs_item.path) : () => false"
                      :class="{ 'active-crumb': drive_bread_crumbs_item.is_active, 'clickable-crumb': !drive_bread_crumbs_item.is_active }">
                      <span v-if="drive_bread_crumbs_item.name == '/'">
                          {{ drive_bread_crumbs_item.name }} Home
                      </span>
                      <span v-else>
                          {{ drive_bread_crumbs_item.name }}
                      </span>
                  </b-breadcrumb-item>
              </template>
          </b-breadcrumb>
          <!-- Folders -->
          <div v-if="share_point_db_settings_not_found" class="d-flex justify-center items-center">
              <h5 class="text-center">
                  Unable to access the SharePoint drive because the necessary SharePoint factory settings are not
                  configured.
              </h5>
          </div>
          <div 
            v-else 
            class="container-fluid mt-3" 
            style="background-color: #eeeeee;" 
            @dragover.prevent="handleContainerDragOver"
            @dragleave.prevent="handleContainerDragLeave"
            @drop.prevent="handleContainerDrop"
            :class="{ 'drop-highlight': isDragging && !isMovingItems }">
              <div
                class="position-relative"
                ref="fileContainer"
                @mousedown="handleMouseDown"
                @mousemove="updateSelection"
                @contextmenu.prevent="handleContainerContextMenu($event)"
                :class="{ 'drop-highlight': isDragging && !isMovingItems }">
                <div class="d-flex flex-wrap mb-4 gap-2">
                  <template v-if="drive_items?.length > 0">
                    <template v-for="drive_item in drive_items">
                      <div 
                        draggable="true" 
                        :key="drive_item.name"
                        class="folder-card d-flex flex-column align-items-center justify-content-center p-3 me-3 mb-3 file-item"
                        @dblclick="handleItemDoubleClick(drive_item)"
                        @contextmenu.prevent="handleContextMenu($event, drive_item)"
                        @click="handleItemClick($event, drive_item)"
                        @dragstart="handleDragStart($event, drive_item)"
                        @dragover.prevent="handleDragOver($event, drive_item)"
                        @dragleave.prevent="handleDragLeave($event, drive_item)"
                        @drop.prevent="handleDrop($event, drive_item)"
                        @dragend="handleDragEnd"
                        :title="`${drive_item.name}, ${drive_item.size}MB`"
                        :class="{ 
                          'selected': selectedFiles.includes(drive_item),
                          'drag-over': dragOverItem === drive_item && drive_item.type === 'folder'
                        }">
                        <img :src="getIcon(drive_item)" alt="Icon" style="object-fit: cover" />
                        <p class="text-center mb-0 text-wrap">{{ drive_item.name }}</p>
                        <CButton class="p-0 border-0" color="light" size="sm"
                          @click.stop="handleContextMenu($event, drive_item)">
                          <i class="bi bi-three-dots-vertical"></i>
                        </CButton>
                      </div>
                    </template>
                  </template>
                  <div class="drop-container" v-else>
                    <h2>Drop files here</h2>
                  </div>
                </div>
                
                <div
                  v-if="isSelecting"
                  class="selection-box"
                  :style="{
                    left: selectionBox.left + 'px',
                    top: selectionBox.top + 'px',
                    width: selectionBox.width + 'px',
                    height: selectionBox.height + 'px',
                  }"
                ></div>
              </div>
          </div>

          <!-- Context Menu -->
          <div v-if="contextMenu.visible" tabindex="-1" ref="contextMenuRef" @blur="closeContextMenu" :style="contextMenu.style"
              class="context-menu">
              <ul>
                  <li @click="refreshDirectory">
                      Refresh
                  </li>
                  <li @click="handleDriveItemAction('cut')" v-if="contextMenu.item">
                      Cut
                  </li>
                  <li @click="handleDriveItemAction('copy')" v-if="contextMenu.item">
                      Copy
                  </li>
                  <li v-if="item_action_info && ['cut', 'copy'].includes(item_action_info.action)"
                      @click="handleDriveItemAction('paste')">
                      Paste
                  </li>
                  <li @click="openRenameModal" v-if="contextMenu.item && !Array.isArray(contextMenu.item)">
                      Rename
                  </li>
                  <li @click="showDeleteConfirmModal" v-if="contextMenu.item">
                      Delete
                  </li>
                  <li v-if="contextMenu.item && !Array.isArray(contextMenu.item) && contextMenu.item.type === 'file' && contextMenu.item.download_url">
                      <a :href="contextMenu.item.download_url" download class="text-dark">
                          <CIcon name="cilCloudDownload" class="mr-2 text-dark" />Download
                      </a>
                  </li>
              </ul>
          </div>

          <!-- File Input (hidden) -->
          <input ref="fileInput" type="file" multiple style="display: none"
              @change="handleSharePointFilesChangeEvent" />

          <!-- Create Folder Modal -->
          <b-modal v-model="show_create_folder_modal" title="Create New Folder"
              @ok="handleDriveItemAction('new_folder')">
              <b-form-input v-model="new_folder_name" placeholder="Enter folder name" />
          </b-modal>

          <!-- Confirmation Dialog -->
          <b-modal v-model="show_delete_confirm" title="Confirm Delete" @ok="handleDriveItemAction('delete')">
              <p v-if="Array.isArray(contextMenu.item) && contextMenu.item.length > 1">
                  Are you sure you want to delete these {{ contextMenu.item.length }} items?
              </p>
              <p v-else>
                  Are you sure you want to delete this item?
              </p>
          </b-modal>
          <b-modal title="Upload in progress!" @ok="handleUploadCancelConfirm(true)" v-model="show_upload_cancel_confirm">
            <p>Current uploading files will be cancelled! Are you sure you want to leave site?</p>
          </b-modal>
          <!-- Rename Dialog -->
          <b-modal v-model="show_rename_modal" title="Rename Item" @ok="renameItem">
              <b-form-input v-model="drive_item_new_name" placeholder="Enter new file name" />
          </b-modal>
          <b-modal v-model="show_conflict_modal" title="File Conflict Detected" @ok="resolveConflict(true)" @cancel="resolveConflict(false)">
            <p>One or more files with the same name already exist in the destination folder.</p>
            <p>Would you like to rename the files automatically to avoid conflicts?</p>
            <template #modal-footer="{ ok, cancel }">
              <b-button variant="secondary" @click="cancel()">
                Cancel Operation
              </b-button>
              <b-button variant="primary" @click="ok()">
                Rename Files
              </b-button>
            </template>
          </b-modal>
      </div>
      <div v-if="Object.keys($store.state.sharepoint.uploadProgress).length > 0 || preparingUploads.length > 0" class="upload-progress-container">
        <div class="d-flex justify-content-between pb-2">
          <h5>Upload Progress:</h5>
        </div>
        <div v-for="(progress, fileName) in $store.state.sharepoint.uploadProgress" :key="fileName" class="upload-item">
          <sharepoint-upload-progress-bar
            :progress="progress"
            :file-name="fileName"
            :preparing="$store.state.sharepoint.uploadPreparation[fileName]"
          />
          <button @click="cancelUpload(fileName)" class="cancel-button float-right">Cancel</button>
        </div>
        <div v-for="item in preparingUploads" :key="item.fileName" class="upload-item">
          <sharepoint-upload-progress-bar
            :progress="0"
            :file-name="item.fileName"
            :preparing="true"
          />
          <button @click="cancelUpload(item.fileName)" class="cancel-button float-right">Cancel</button>
        </div>
      </div>
      <div v-if="$store.state.sharepoint.multiFileUpload.inProgress" class="multi-file-upload-container">
        <h5>Files {{ $store.state.sharepoint.multiFileUpload.completed }} of {{ $store.state.sharepoint.multiFileUpload.total }} uploading</h5>
        <div class="multi-file-progress">
          <div class="multi-file-progress-bar" :style="{ width: `${($store.state.sharepoint.multiFileUpload.completed / $store.state.sharepoint.multiFileUpload.total) * 100}%` }"></div>
        </div>
      </div>
  </div>
</template>

<script>
import { http } from "@/config/httpCommon";
import { handleResponseException } from "@/helpers/Helpers";
import axios from 'axios';
import SharepointUploadProgressBar from "@/components/SharepointUploadProgressBar.vue";
export default {
  components: {
    SharepointUploadProgressBar,
  },
  props: ['order', 'order_id', 'order_factories'],
  data() {
    return {
          show_loader: false,
          drive_items: [],
          drive_items_parent_info: {},
          drive_bread_crumbs: [{
              is_active: true,
              name: "Home",
              path: ""
          }],
          contextMenu: { visible: false, style: {}, item: null },
          isDragging: false,
          show_create_folder_modal: false,
          show_delete_confirm: false,
          new_folder_name: "",
          show_rename_modal: false,
          active_factory: {},
          item_action_info: null,
          drive_item_new_name: "",
          share_point_files_upload_info: {
            total: 0,
            uploaded: 0,
            failed: 0,
            remaining: 0,
          },
          share_point_db_settings_not_found: false,
          fileContainer: null,
          isSelecting: false,
          selectionBox: {
            left: 0,
            top: 0,
            width: 0,
            height: 0
          },
          startX: 0,
          startY: 0,
          containerRect: null,
          selectedFiles: [],
          isRightClick: false,
          ctrlKeyPressed: false,
          shiftKeyPressed: false,
          share_point_access_token: null,
          share_point_active_factory_settings: null,
          lastClickTime: 0,
          clickDelay: 300,
          pendingClick: null,
          draggedItems: [],
          dragOverItem: null,
          dragCounter: 0,
          show_conflict_modal: false,
          conflictOperation: null,
          conflictItems: [],
          conflictTarget: null,
          moveOperationInProgress: false,
          isMovingItems: false,
          isUploading: false,
          show_upload_cancel_confirm: false,
          preventUnload: false,
      };
  },
  async mounted() {
      if (this.order_factories?.length > 0) {
          this.active_factory = this.order_factories[0]
      }
      document.addEventListener("click", this.closeContextMenu);
      document.addEventListener('mouseup', this.endSelection);
      document.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('keyup', this.handleKeyUp);
      window.addEventListener("beforeunload", this.handleBeforeUnload);
      this.getSharePointFilesAtPath()
      this.share_point_files_upload_info = this.getFileUploadInfoDefaultObject()
      this.$store.dispatch('getFactorySharepointInfo', { order_factories: this.order_factories, active_factory: this.active_factory })
  },
  beforeDestroy() {
      document.removeEventListener("click", this.closeContextMenu);
      document.removeEventListener('mouseup', this.endSelection);
      document.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('keyup', this.handleKeyUp);
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },
  computed: {
    preparingUploads() {
      return Object.entries(this.$store.state.sharepoint.uploadPreparation)
        .filter(([fileName, preparing]) => preparing && !this.$store.state.sharepoint.uploadProgress[fileName])
        .map(([fileName]) => ({ fileName }));
    },
    fileThumbnails() {
      return this.$store.getters['getFileThumbnails'];
    }
  },
  methods: {
      getFileUploadInfoDefaultObject() {
          return {
              total: 0,
              uploaded: 0,
              remaining: 0,
              failed: 0
          }
      },
      handleFactoryUpdate(updated_factory) {
          this.active_factory = updated_factory
          this.getSharePointFilesAtPath()
          this.$store.dispatch('getFactorySharepointInfo', { order_factories: this.order_factories, active_factory: this.active_factory })
      },
      refreshDirectory() {
          this.contextMenu.visible = false;
          this.getSharePointFilesAtPath(this.drive_items_parent_info.path)
      },
      getSharePointFilesAtPath(drive_items_path = "/") {
          this.show_loader = true
          http.post(`order/${this.order_id}/share/point/files`, {
              order_id: this.order_id, drive_items_path: drive_items_path, factory_id: this.active_factory.id
          }).then(successResponse => {
              this.show_loader = false
              const response_result = successResponse.data.result
              this.drive_items_parent_info = response_result.share_point_drive_item_parent_path_info
              this.drive_items = response_result.share_point_drive_path_items
              this.drive_bread_crumbs = response_result.drive_path_item_bread_crumbs
              this.share_point_db_settings_not_found = false
          }).catch((errorResponse) => {
              this.show_loader = false
              this.share_point_db_settings_not_found = true
              this.drive_items = []
              this.drive_items_parent_info = {}
              this.drive_bread_crumbs = []
              handleResponseException(errorResponse)
          });
      },
      async handleDriveItemAction(action) {
        this.contextMenu.visible = false;
        if (["copy", "cut", "delete", "rename"].includes(action)) {
          this.item_action_info = { drive_item: this.contextMenu.item, action }
        }
        if (["new_folder", "rename", "paste", 'delete'].includes(action)) {
          if (["new_folder"].includes(action)) {
            this.selectedFiles.push("new_folder");
          }
          let action_payload;
          let deletedCount = 0;
          const promises = this.selectedFiles.map(async (file) => {
            this.show_loader = true;
            action_payload = this.getDriveItemActionPayload(action, file);
            try {
              await this.$store.dispatch('handleSharepointDriveItemAction', action_payload);
              if (action === 'delete') {
                deletedCount++;
              }
            } catch (err) {
              this.contextMenu.visible = false;
              this.show_loader = false;
              handleResponseException(err);
            }
          });

          await Promise.all(promises);

          if (action === 'delete' && deletedCount > 0) {
            this.showToast(`${deletedCount} item${deletedCount > 1 ? 's' : ''} deleted successfully`, "SUCCESS");
          } else if (action !== 'delete') {
            this.showToast("Action completed", "SUCCESS");
          }

          this.handleDriveItemActionCompletion(action, action_payload);
        }
      },

      handleDriveItemActionCompletion(action, action_payload) {
        switch (action) {
          case "new_folder":
            this.new_folder_name = "";
            this.refreshDirectory();
            break;
          case "rename":
            if (this.item_action_info.drive_item) {
              this.item_action_info.drive_item.name = action_payload.drive_item_new_name;
            }
            this.show_loader = false;
            break;
          case "delete":
            this.show_delete_confirm = false;
            this.selectedFiles = [];
            this.refreshDirectory();
          break;
          default:
            this.refreshDirectory();
        }
      },
      getDriveItemActionPayload(action, file) {
          switch (action) {
              case "new_folder":
                  return {
                      target_item_action: action,
                      destination_item_id: this.drive_items_parent_info.id,
                      folder_name: this.new_folder_name,
                      factory_id: this.active_factory.id
                  };
              case "rename": {
                  let drive_item_full_name = this.drive_item_new_name;
                  if (this.item_action_info.drive_item.type === "file") {
                      drive_item_full_name = `${drive_item_full_name}.` + file.name.split(".").pop();
                  }
                  return {
                      target_item_action: this.item_action_info.action,
                      drive_item_new_name: drive_item_full_name,
                      target_item_id: this.item_action_info.drive_item.id,
                      factory_id: this.active_factory.id
                  };
              }
              case "paste":
                  return {
                      target_item_action: this.item_action_info.action,
                      target_item_id: file.id,
                      destination_item_action: action,
                      destination_item_id: this.drive_items_parent_info.id,
                      factory_id: this.active_factory.id
                  };
              case "delete":
                  return {
                      target_item_action: this.item_action_info.action,
                      target_item_id: file.id,
                      factory_id: this.active_factory.id
                  };
              default:
                  return {};
          }
      },
      handleItemDoubleClick(drive_item) {
        if (this.pendingClick) {
          clearTimeout(this.pendingClick);
          this.pendingClick = null;
        }
        
        if (drive_item.type === "folder") {
          const drive_item_path = drive_item.parent_info.path === "/" ? drive_item.name : `${drive_item.parent_info.path}/${drive_item.name}`
          this.getSharePointFilesAtPath(drive_item_path)
        } else {
          this.downloadFile(drive_item);
        }
      },
      downloadFile(drive_item) {
          const downloadUrl = `${drive_item.download_url}`;
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = drive_item.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      },
      handleSharePointFilesChangeEvent(event) {
          this.uploadSharePointFiles(Array.from(event.target.files))
      },
      handleSharePointFilesDropEvent(event) {
        this.uploadSharePointFiles(Array.from(event.dataTransfer.files));
        this.isDragging = false;
      },
      handleBeforeUnload(event) {
        if (this.isUploading) {
          this.preventUnload = true;
          this.show_upload_cancel_confirm = true;
          event.preventDefault(); // Cancel the event
          event.returnValue = ''; // Chrome requires returnValue to be set
          this.$nextTick(() => {
            this.$bvModal.show('upload-cancel-confirm-modal');
          });
        }
      },

      handleUploadCancelConfirm(confirm) {
        if (confirm) {
          this.cancelAllUploads()
          return
        }
        return;
      },

      cancelAllUploads() {
        Object.keys(this.$store.state.sharepoint.uploadProgress).forEach(fileName => {
          this.$store.dispatch('cancelUpload', fileName);
        });
      },
      uploadSharePointFiles(files) {
        this.isUploading = true
        if (files?.length > 0) {
          this.show_loader = true;
          
          // Initialize multi-file upload tracking if there are multiple small files
          const smallFiles = files.filter(file => file.size < 250 * 1024 * 1024);
          if (smallFiles.length > 1) {
            this.$store.commit('SET_MULTI_FILE_UPLOAD', { total: smallFiles.length });
          }
        }
        
        this.share_point_files_upload_info.total = files?.length;
        files.forEach((file, fileIndex) => {
          const payload = {
            destination_item_id: this.drive_items_parent_info.id,
            file_name: file.name,
            file: file
          };
          
          if (file.size >= 250 * 1024 * 1024) {
            this.isUploading = true;
            this.$store.dispatch('uploadLargeSharepointFile', payload)
              .then(this.handleUploadSuccess)
              .catch(this.handleUploadError)
              .finally(() => this.isUploading = false)
          } else {
            this.$store.dispatch('uploadSharepointFile', payload)
              .then(this.handleUploadSuccess)
              .catch(this.handleUploadError);
          }
        });
        this.show_loader = false;
      },
      handleUploadSuccess(response) {
        if (response && response.canceled) {
          this.showToast("Upload canceled", "INFO");
        } else {
          this.share_point_files_upload_info.uploaded++;
          this.updateRemainingUploads();
          this.handleAllFilesUploaded();
        }
      },

      handleUploadError(error) {
        if (axios.isCancel(error)) {
          this.showToast("Upload canceled", "INFO");
        } else {
          this.share_point_files_upload_info.failed++;
          this.updateRemainingUploads();
          this.handleAllFilesUploaded();
          handleResponseException(error);
        }
      },

      updateRemainingUploads() {
        this.share_point_files_upload_info.remaining = 
        this.share_point_files_upload_info.total - 
        this.share_point_files_upload_info.uploaded - 
        this.share_point_files_upload_info.failed;
      },
      handleAllFilesUploaded() {
          if (this.share_point_files_upload_info.uploaded + this.share_point_files_upload_info.failed === this.share_point_files_upload_info.total) {
              this.show_loader = false
              if (this.share_point_files_upload_info.failed > 0) {
                  this.showToast(`${this.share_point_files_upload_info.failed} files failed to upload`, "ERROR");
              }
              if (this.share_point_files_upload_info.uploaded > 0) {
                  this.showToast(`${this.share_point_files_upload_info.uploaded} files uploaded successfully`, "SUCCESS");
              }
              
              // Add a delay before resetting the multi-file upload state
              setTimeout(() => {
                  this.$store.commit('RESET_MULTI_FILE_UPLOAD');
                  this.share_point_files_upload_info = this.getFileUploadInfoDefaultObject();
              }, 2000);
              
              this.refreshDirectory();
          }
      },
      triggerFileInput() {
          this.$refs.fileInput.click();
      },
      openCreateFolderModal() {
          this.show_create_folder_modal = true;
      },
      openContextMenu(item, event) {
          this.contextMenu = {
              visible: true,
              style: { top: `${event.clientY}px`, left: `${event.clientX}px` },
              item: this.selectedFiles.length > 0 ? this.selectedFiles : item,
          };
          event.stopPropagation();
          
          this.$nextTick(() => {
              if (this.$refs.contextMenuRef) {
                  this.$refs.contextMenuRef.focus();
              }
          });
      },
      closeContextMenu(event) {
          if (!event || !event.target || !event.target.closest(".context-menu")) {
              this.contextMenu.visible = false;
          }
      },
      renameItem() {
          if (!this.drive_item_new_name || !this.drive_item_new_name.trim()) {
              this.showToast("File name cannot be empty", "ERROR");
              return;
          }
          
          this.drive_item_new_name = this.drive_item_new_name.trim();
          this.handleDriveItemAction("rename");
      },
      openRenameModal() {
          const drive_item = this.contextMenu.item;
          if (!drive_item) return;
          
          let drive_item_name = drive_item.name;
          let extension = "";
          if (drive_item.type === "file") {
              extension = drive_item_name.split(".").pop();
              drive_item_name = drive_item_name.slice(0, -(extension?.length + 1));
          }
          this.drive_item_new_name = drive_item_name;
          this.contextMenu.visible = false;
          this.show_rename_modal = true;
      },
      showDeleteConfirmModal() {
          this.item_action_info = { drive_item: this.contextMenu.item, action: "delete" };
          this.show_delete_confirm = true;
          this.contextMenu.visible = false;
      },
      getIcon(drive_item) {
        if (!drive_item.extension) {
          drive_item.extension = "folder";
        }
        
        // Define icon mapping for fallback
        const iconMapping = {
          folder: "https://cdn-icons-png.flaticon.com/512/716/716784.png", // Folder icon
          jpg: "https://cdn-icons-png.flaticon.com/512/136/136524.png", // JPG icon
          jpeg: "https://cdn-icons-png.flaticon.com/512/136/136524.png", // JPG icon
          png: "https://cdn-icons-png.flaticon.com/512/136/136524.png", // PNG icon
          zip: "https://cdn-icons-png.flaticon.com/512/12075/12075407.png", // ZIP icon - Updated to better zip icon
          rar: "https://cdn-icons-png.flaticon.com/512/2306/2306199.png", // RAR icon
          docx: "https://cdn-icons-png.flaticon.com/512/281/281760.png", // DOCX icon
          doc: "https://cdn-icons-png.flaticon.com/512/281/281760.png", // DOC icon
          pdf: "https://cdn-icons-png.flaticon.com/512/337/337946.png", // PDF icon
          xlsx: "https://cdn-icons-png.flaticon.com/512/732/732220.png", // Excel icon
          xls: "https://cdn-icons-png.flaticon.com/512/732/732220.png", // Excel icon
          pptx: "https://cdn-icons-png.flaticon.com/512/888/888910.png", // PowerPoint icon
          ppt: "https://cdn-icons-png.flaticon.com/512/888/888910.png", // PowerPoint icon
          mp4: "https://cdn-icons-png.flaticon.com/512/136/136548.png", // MP4 icon
          mp3: "https://cdn-icons-png.flaticon.com/512/136/136545.png", // MP3 icon
          txt: "https://cdn-icons-png.flaticon.com/512/136/136538.png", // TXT icon
          json: "https://cdn-icons-png.flaticon.com/512/136/136528.png" // JSON icon
        };
        
        // Check if we should try to get a thumbnail
        if (['jpeg', 'jpg', 'png', 'gif', 'bmp', 'tiff', 'pdf', 'docx', 'pptx', 'xlsx'].includes(drive_item.extension.toLowerCase())) {
          // Trigger thumbnail fetch in the background, but don't wait for it
          this.$store.dispatch('getFileThumbnails', drive_item);
          
          // Check if we already have a thumbnail
          const existingThumbnail = this.$store.getters['getFileThumbnails'](drive_item.id);
          
          if (existingThumbnail && existingThumbnail.large && existingThumbnail.large.url) {
            // Use existing thumbnail if available
            return existingThumbnail.large.url;
          }
        }
        
        // Fallback to icon mapping if no thumbnail
        const genericIcon = "https://cdn-icons-png.flaticon.com/512/137/137473.png"; // Truly generic file icon
        return iconMapping[drive_item.extension.toLowerCase()] || genericIcon;
      },
      async getFactorySharepointInfo(factories) {
          const factory_ids = factories.map(factory => factory.id);
          await http.post('get-sharepoint-factory-info', {factory_ids}).then((res) => {
              this.share_point_access_token = res.data.result.access_token;
              this.share_point_active_factory_settings = res.data.result.factories_sharepoint_settings[this.active_factory.id];
          }).catch((err) => {
              this.share_point_db_settings_not_found = true;
              handleResponseException(err);
          });
      },
      toggleItemSelection(drive_item) {
          const index = this.selectedFiles.findIndex(item => item.name === drive_item.name);
          if (index === -1) {
              this.selectedFiles.push(drive_item);
          } else {
              this.selectedFiles.splice(index, 1);
          }
      },
      selectRange(event, drive_item) {
          if (event.shiftKey && this.selectedFiles.length) {
              let lastSelected = this.selectedFiles[this.selectedFiles.length - 1];
              let startIndex = this.drive_items.findIndex(item => item.name === lastSelected.name);
              let endIndex = this.drive_items.findIndex(item => item.name === drive_item.name);
              
              let range = this.drive_items.slice(
                  Math.min(startIndex, endIndex),
                  Math.max(startIndex, endIndex) + 1
              );
              
              this.selectedFiles = range;
          } else {
              this.toggleItemSelection(drive_item);
          }
      },
      handleContextMenu(event, item) {
        if (this.selectedFiles.length === 0 || !this.selectedFiles.includes(item)) {
          this.selectedFiles = [item];
        }
        
        this.contextMenu = {
          visible: true,
          style: { top: `${event.clientY}px`, left: `${event.clientX}px` },
          item: this.selectedFiles.length > 1 ? this.selectedFiles : item,
        };
        
        this.$nextTick(() => {
          if (this.$refs.contextMenuRef) {
              this.$refs.contextMenuRef.focus();
          }
        });
        
        event.stopPropagation();
      },
      handleContainerContextMenu(event) {
        if (!event.target.closest('.file-item')) {
          this.contextMenu = {
            visible: true,
            style: { top: `${event.clientY}px`, left: `${event.clientX}px` },
            item: null
          };
          
          this.$nextTick(() => {
            if (this.$refs.contextMenuRef) {
                this.$refs.contextMenuRef.focus();
            }
          });
        }
      },
      startSelection(event) {
        if (!this.$refs.fileContainer) return;

        this.containerRect = this.$refs.fileContainer.getBoundingClientRect();

        this.isSelecting = true;
        this.startX = event.clientX - this.containerRect.left;
        this.startY = event.clientY - this.containerRect.top;

        this.selectionBox.left = this.startX;
        this.selectionBox.top = this.startY;
        this.selectionBox.width = 0;
        this.selectionBox.height = 0;

        if (!this.ctrlKeyPressed && !this.shiftKeyPressed) {
          this.selectedFiles = [];
        }
      },
      checkSelection() {
        if (!this.$refs.fileContainer || !this.drive_items.length) return;

        const fileElements = this.$refs.fileContainer.querySelectorAll('.file-item');
        
        const newlySelectedFiles = this.drive_items
          .filter((file, index) => {
            if (!fileElements[index]) return false;

            const rect = fileElements[index].getBoundingClientRect();

            const fileLeft = rect.left - this.containerRect.left;
            const fileTop = rect.top - this.containerRect.top;

            return (
              fileLeft < this.selectionBox.left + this.selectionBox.width &&
              fileLeft + rect.width > this.selectionBox.left &&
              fileTop < this.selectionBox.top + this.selectionBox.height &&
              fileTop + rect.height > this.selectionBox.top
            );
          });
          
        if (this.ctrlKeyPressed) {
          newlySelectedFiles.forEach(file => {
            const index = this.selectedFiles.findIndex(f => f.id === file.id);
            if (index === -1) {
              this.selectedFiles.push(file);
            } else {
              this.selectedFiles.splice(index, 1);
            }
          });
        } else {
          this.selectedFiles = this.ctrlKeyPressed 
            ? [...this.selectedFiles, ...newlySelectedFiles.filter(file => !this.selectedFiles.includes(file))]
            : newlySelectedFiles;
        }
      },
      updateSelection(event) {
        if (!this.isSelecting || !this.containerRect) return;

        const currentX = event.clientX - this.containerRect.left;
        const currentY = event.clientY - this.containerRect.top;

        this.selectionBox.left = Math.min(this.startX, currentX);
        this.selectionBox.top = Math.min(this.startY, currentY);
        this.selectionBox.width = Math.abs(this.startX - currentX);
        this.selectionBox.height = Math.abs(this.startY - currentY);

        this.checkSelection();
      },
      handleMouseDown(event) {
        if (event.button === 2) {
          this.isRightClick = true;
          return;
        }
        
        this.isRightClick = false;
        
        if (!event.target.closest('.file-item') || (this.ctrlKeyPressed || this.shiftKeyPressed)) {
          this.startSelection(event);
        }
      },
      handleItemClick(event, item) {
        if (this.isSelecting) return;
        
        if (this.pendingClick) {
          clearTimeout(this.pendingClick);
          this.pendingClick = null;
        }
        
        const clickTime = new Date().getTime();
        const isDoubleClick = clickTime - this.lastClickTime < this.clickDelay;
        this.lastClickTime = clickTime;
        
        if (isDoubleClick) {
          return;
        }
        
        this.pendingClick = setTimeout(() => {
          if (this.ctrlKeyPressed) {
            const index = this.selectedFiles.indexOf(item);
            if (index === -1) {
              this.selectedFiles.push(item);
            } else {
              this.selectedFiles.splice(index, 1);
            }
            event.preventDefault();
            return;
          }
          
          if (this.shiftKeyPressed) {
            const lastSelected = this.selectedFiles[this.selectedFiles.length - 1];
            const lastIndex = this.drive_items.indexOf(lastSelected);
            const currentIndex = this.drive_items.indexOf(item);
            
            const start = Math.min(lastIndex, currentIndex);
            const end = Math.max(lastIndex, currentIndex);
            
            this.selectedFiles = this.drive_items.slice(start, end + 1);
            event.preventDefault();
            return;
          }
          
          if (!this.ctrlKeyPressed && !this.shiftKeyPressed) {
            this.selectedFiles = [item];
          }
        }, this.clickDelay);
      },
      endSelection() {
        this.isSelecting = false;
      },
      handleKeyDown(event) {
        if (event.key === 'Control' || event.key === 'Meta') {
          this.ctrlKeyPressed = true;
        } else if (event.key === 'Shift') {
          this.shiftKeyPressed = true;
        }
      },
      handleKeyUp(event) {
        if (event.key === 'Control' || event.key === 'Meta') {
          this.ctrlKeyPressed = false;
        } else if (event.key === 'Shift') {
          this.shiftKeyPressed = false;
        }
      },
      handleContainerDragOver(event) {
        if (!this.isMovingItems) {
          this.isDragging = true;
          event.dataTransfer.dropEffect = 'copy';
        }
      },
      handleContainerDragLeave(event) {
        if (!this.isMovingItems && !event.relatedTarget?.closest('.container-fluid')) {
          this.isDragging = false;
        }
      },
      handleContainerDrop(event) {
        if (!this.isMovingItems) {
          this.handleSharePointFilesDropEvent(event);
        }
        this.isDragging = false;
        this.isMovingItems = false;
      },
      handleDragLeave(event, item) {
        if (item && this.dragOverItem === item) {
          this.dragOverItem = null;
        }
      },
      showToast(message, type = "INFO") {
        if (this.$toast) {
          this.$toast[type.toLowerCase()](message);
        } else if (type === "ERROR") {
          alert(message);
        } else if (type === "SUCCESS" || type === "INFO") {
          console.info(message);
        }
      },
      handleDragStart(event, item) {
        this.isMovingItems = true;
        if (this.pendingClick) {
          clearTimeout(this.pendingClick);
          this.pendingClick = null;
        }
        
        if (!this.selectedFiles.includes(item)) {
          this.selectedFiles = [item];
        }
        
        this.draggedItems = [...this.selectedFiles];
        
        if (this.selectedFiles.length > 1) {
          const dragImage = document.createElement('div');
          dragImage.className = 'drag-image';
          dragImage.innerHTML = `<div class="drag-count">${this.selectedFiles.length} items</div>`;
          document.body.appendChild(dragImage);
          event.dataTransfer.setDragImage(dragImage, 25, 25);
          setTimeout(() => {
            document.body.removeChild(dragImage);
          }, 0);
        }
        
        event.dataTransfer.setData('text/plain', JSON.stringify({
          action: 'move',
          items: this.draggedItems.map(item => item.id)
        }));
        
        event.dataTransfer.effectAllowed = 'move';
      },
      
      handleDragOver(event, item) {
        if (item.type === 'folder') {
          event.dataTransfer.dropEffect = 'move';
          this.dragOverItem = item;
        } else {
          event.dataTransfer.dropEffect = 'none';
        }
      },
      
      handleDrop(event, targetFolder) {
        if (targetFolder.type !== 'folder') {
          return;
        }
        
        this.dragOverItem = null;
        
        try {
          const data = JSON.parse(event.dataTransfer.getData('text/plain'));
          
          if (data.action === 'move' && data.items && data.items.length > 0) {
            const itemsToMove = this.draggedItems.filter(item => 
              !(item.id === targetFolder.id || 
                (item.type === 'folder' && targetFolder.parent_info.path.includes(item.name)))
            );
            
            if (itemsToMove.length === 0) {
              this.showToast("Cannot move a folder into itself or its subfolder", "ERROR");
              return;
            }
            
            this.executeMoveOperation(itemsToMove, targetFolder, false);
          }
        } catch (error) {
          console.error("Error processing drop:", error);
        }
      },
      
      handleDragEnd() {
        this.draggedItems = [];
        this.dragOverItem = null;
        this.isMovingItems = false;
      },
      
      executeMoveOperation(itemsToMove, targetFolder, forceRename = false) {
        if (this.moveOperationInProgress) {
          return;
        }
        
        this.moveOperationInProgress = true;
        this.show_loader = true;
        
        const movePromises = itemsToMove.map(item => {
          return this.$store.dispatch('moveSharepointItem', {
            itemId: item.id,
            destinationFolderId: targetFolder.id,
            handleConflict: forceRename
          });
        });
        
        Promise.all(movePromises)
          .then((responses) => {
            this.showToast(`${itemsToMove.length} item(s) moved successfully`, "SUCCESS");
            this.refreshDirectory();
            this.isDragging = false;
            this.moveOperationInProgress = false;
          })
          .catch(error => {
            this.show_loader = false;
            this.isDragging = false;
            this.moveOperationInProgress = false;
            
            if (error.response && error.response.status === 409) {
              this.handleConflict(itemsToMove, targetFolder);
            } else {
              handleResponseException(error);
            }
          });
      },
      handleConflict(itemsToMove, targetFolder) {
        this.show_conflict_modal = true;
        this.conflictItems = itemsToMove;
        this.conflictTarget = targetFolder;
      },

      resolveConflict(shouldRename) {
        this.show_conflict_modal = false;
        
        if (shouldRename) {
          this.executeMoveOperation(this.conflictItems, this.conflictTarget, true);
        } else {
          this.showToast("Move operation canceled", "INFO");
        }
        
        this.conflictItems = [];
        this.conflictTarget = null;
      },
      cancelUpload(fileName) {
        this.$store.dispatch('cancelUpload', fileName);
      },
  },
};
</script>

<style lang="scss" scoped>
.drop-highlight {
border: 2px dashed #2EB85C;
background-color: #2EB85C70;
position: relative;
}

.drop-container {
height: 100%;
width: 100%;
display: flex;
justify-content: center;
align-items: center;
color: #2EB85C;
min-height: 120px
}

.context-menu {
position: absolute;
background: #ffffffd2;
border: 1px solid #ddd;
box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
z-index: 10;
width: 120px;
border-radius: 8px;
overflow: hidden;

ul {
  list-style: none;
  padding: 0;
  margin: 0;

  .bulk-operation-header {
    font-weight: bold;
    background: #f0f0f0;
    color: #333;
    padding: 8px 10px;
    cursor: default;
    
    &:hover {
      background: #f0f0f0;
      color: #333;
    }
  }

  li {
    padding: 6px 10px;
    cursor: pointer;

    &:hover {
      background: #2eb85cc2;
      color: white;

      a {
        color: white !important;

        svg {
          polygon,
          path {
            fill: white !important;
          }
        }
      }
    }
  }
}
}

.folder-card {
flex: 1 1 calc(120px);
max-width: 150px;
width: 100%;
height: auto;
border-radius: 8px;
transition: all 0.3s;
cursor: pointer;
display: flex;
flex-direction: column;
align-items: center;
justify-content: center;

&:hover {
  background: white;
}

img {
  width: 70px;
  height: 70px;
  object-fit: cover;
  background: none;
}

p {
  word-wrap: break-word;
  overflow-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  margin: 0px;
  font-size: 14px;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
}

.custom-breadcrumb {
background: #eeeeee;
border-radius: 8px;
padding: 10px 15px;
box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

.breadcrumb-item {
  font-size: 14px;
  display: flex;
  align-items: center;
  color: #6c757d;

  i {
    margin-right: 5px;
  }

  &.clickable-crumb {
    cursor: pointer;
    color: #007bff;

    &:hover {
      color: #0056b3;
      text-decoration: underline;
    }
  }

  &.active-crumb {
    color: #212529;
    font-weight: bold;

    i {
      color: #007bff;
    }
  }
}
}

.spinner-container {
height: 200px;
width: 100%;
background-color: #ffffff53;

.spinner {
  height: 50px;
  width: 50px;
  border-radius: 50%;
  border: 5px solid #2eb85ca8;
  border-top-color: transparent;
  animation: spin .5s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
}

.factories_list {
display: flex;
width: 100%;
flex-wrap: wrap;
border-bottom: 2px solid #6c757d;

button {
  background-color: white;
  border: none;
  outline: none;
  color: black;
  font-weight: 500;
  padding: 6px 12px;
  box-shadow: inset 0px 0px 5px 1px rgba(0, 0, 0, 0.418);

  &.active_factory {
    background-color: #2eb85c;
    color: white;
    box-shadow: none;
  }

  &:first-of-type {
    border-top-left-radius: 4px;
  }

  &:last-of-type {
    border-top-right-radius: 4px;
  }
}
}

.file-container {
position: relative;
width: 100%;
height: 100vh;
overflow: hidden;
}

.selected {
background: #E6F3F1;
border: 1px solid #219F84;
}

.selection-box {
position: absolute;
background: rgba(0, 120, 215, 0.3);
border: 1px solid #0078d7;
pointer-events: none;
}

.selection-count {
position: fixed;
bottom: 20px;
right: 20px;
background: rgba(46, 184, 92, 0.9);
color: white;
padding: 8px 16px;
border-radius: 20px;
font-weight: bold;
box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
z-index: 1000;
transition: all 0.3s ease;
}

.drag-over {
background-color: #2EB85C30 !important;
border: 2px dashed #2EB85C !important;
}

.drag-image {
position: fixed;
top: -1000px;
left: -1000px;
z-index: -1;
background: rgba(46, 184, 92, 0.8);
border-radius: 50%;
width: 50px;
height: 50px;
display: flex;
align-items: center;
justify-content: center;
}

.drag-count {
color: white;
font-weight: bold;
font-size: 12px;
text-align: center;
}
.upload-progress-container {
position: fixed;
bottom: 20px;
right: 20px;
width: 300px;
max-height: 200px;
overflow-y: auto;
background-color: white;
border: 1px solid #ddd;
border-radius: 5px;
padding: 10px;
box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}
.multi-file-upload-container {
position: fixed;
bottom: 230px;
right: 20px;
width: 300px;
background-color: white;
border: 1px solid #ddd;
border-radius: 5px;
padding: 10px;
box-shadow: 0 2px 10px rgba(0,0,0,0.1);
z-index: 1000;
}

.multi-file-progress {
width: 100%;
height: 20px;
background-color: #f0f0f0;
border-radius: 10px;
overflow: hidden;
margin-top: 5px;
}

.multi-file-progress-bar {
height: 100%;
background-color: #2EB85C;
transition: width 0.3s ease-in-out;
}

.cancel-button {
  margin-left: 10px;
  background-color: #ff4d4f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #ff7875;
  }
}
.drop-highlight {
  border: 2px dashed #2EB85C;
  background-color: #2EB85C70;
  position: relative;
}
</style>